/**
 * The live size of one element, also of one that mounts after the first render.
 *
 * Each later size commits before paint, so what is drawn for a size paints in the frame that size
 * arrived in: a size a frame behind leaves the content drawn for a box the element no longer has.
 */

import type { RefObject } from 'react';
import { useLayoutEffect, useState } from 'react';

import { commitBeforePaint } from '@/lib/commitBeforePaint';
import { ElementBox } from '@/types/layout';

interface ElementSize {
    width: number;
    height: number;
}

interface Options {
    /** Default: the content box. */
    box?: ElementBox;
    /** The width until the first size of the content box arrives. */
    initialWidth?: number;
}

function readBorderBox(element: Element): ElementSize {
    const rect = element.getBoundingClientRect();
    return { width: rect.width, height: rect.height };
}

function readEntry(entry: ResizeObserverEntry, box: ElementBox): ElementSize {
    switch (box) {
        case ElementBox.Border:
            return readBorderBox(entry.target);
        case ElementBox.Content:
        default:
            return { width: entry.contentRect.width, height: entry.contentRect.height };
    }
}

export function useElementSize(
    ref: RefObject<Element | null>,
    { box = ElementBox.Content, initialWidth = 0 }: Options = {},
): ElementSize {
    const [size, setSize] = useState<ElementSize>({ width: initialWidth, height: 0 });
    // An effect keyed on the ref object never re-runs when the node mounts late, so the node is
    // read after each render (a same-value setState bails out).
    const [node, setNode] = useState<Element | null>(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- each render, to see a late node
    useLayoutEffect(() => {
        setNode(ref.current);
    });

    useLayoutEffect(() => {
        if (!node) {
            return undefined;
        }
        // The same object while the size holds, so an effect keyed on it does not run again.
        const keep = (nextElementSize: ElementSize): void => setSize((previous) => (
            previous.width === nextElementSize.width &&
            previous.height === nextElementSize.height ? previous : nextElementSize));
        if (box === ElementBox.Border) {
            keep(readBorderBox(node));
        }
        // The first delivery reports the mount: nothing on screen to keep up with.
        let isDelivered = false;
        const observer = new ResizeObserver(([entry]) => {
            if (!entry) {
                return;
            }
            const nextElementSize = readEntry(entry, box);
            if (isDelivered) {
                commitBeforePaint(() => keep(nextElementSize));
            } else {
                isDelivered = true;
                keep(nextElementSize);
            }
        });
        observer.observe(node);
        return () => observer.disconnect();
    }, [node, box]);

    return size;
}
